/**
 * Localised string catalogues. Keys are flat dot-paths so they're easy
 * to grep. Ukrainian (the default) defines the canonical key set —
 * `MessageKey = keyof typeof MESSAGES.uk`; English is the second
 * language, Russian is not offered (owner, 20.10.2026). Missing keys
 * fall back to Ukrainian. `{name}` marks a value (`tf`), keys ending in
 * `.one` / `.few` / `.many` / `.other` are plural forms (`tp`).
 */

import { ACCESS_MESSAGES } from './access.js';
import { ONBOARDING_MESSAGES } from './onboarding.js';
import { OPERATOR_MESSAGES } from './operator.js';
import { SITE_MESSAGES } from './site.js';

export const LOCALES = ['uk', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_LABEL: Record<Locale, string> = {
  uk: 'UK',
  en: 'EN',
};

export const MESSAGES = {
  en: {
    ...SITE_MESSAGES.en,
    ...OPERATOR_MESSAGES.en,
    ...ONBOARDING_MESSAGES.en,
    ...ACCESS_MESSAGES.en,
    'app.brand': 'Phage Energy',
    'nav.menu': 'Menu',
    'nav.lang': 'Language',
    'nav.objects': 'Objects',
    'nav.applications': 'Applications',
    'nav.prices': 'DAM prices',
    'nav.logout': 'Sign out',

    'common.back': 'Back',
    'common.next': 'Next',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.close': 'Close',
    'common.loading': 'Loading…',
    'common.load_error': 'Could not load the data. Reload the page or try again later.',
    'error.generic': 'Something went wrong. Try again.',
    'error.rate_limited': 'Too many attempts. Try again in {min} min.',
    'error.vendor': 'DeyeCloud is not responding. Try again in a few minutes.',
    'error.forbidden': 'Not available for your account.',

    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.password.hint': 'at least 8 characters',
    'auth.login.title': 'Sign in',
    'auth.login.submit': 'Sign in',
    'auth.login.forgot': 'Forgot password?',
    'auth.login.no_account': 'No account?',
    'auth.login.go_signup': 'Sign up',
    'auth.login.expired': 'Your session has ended. Sign in again.',
    'auth.login.reset_ok': 'Password changed. Sign in with the new password.',
    'auth.login.invalid': 'Wrong email or password.',
    'auth.signup.title': 'Sign up',
    'auth.signup.sub': 'After signing up you connect your Deye inverter by its SN.',
    'auth.signup.name': 'First and last name',
    'auth.signup.phone': 'Phone',
    'auth.signup.terms': 'I accept the terms of use and the processing of my data',
    'auth.signup.control':
      'I allow Phage.Energy to charge and discharge the battery by a schedule that I see and can change or switch off',
    'auth.signup.consent_err': 'To continue, accept both items.',
    'auth.signup.submit': 'Create account',
    'auth.signup.have': 'Already have an account?',
    'auth.signup.go_login': 'Sign in',
    'auth.signup.email_taken': 'This email is already registered. Sign in or reset the password.',
    'auth.err.name': 'Enter your first and last name.',
    'auth.err.phone': 'Enter a phone number, e.g. +380 67 123 45 67.',
    'auth.err.email': 'Enter an email, e.g. owner@example.com.',
    'auth.err.password': 'The password needs at least 8 characters.',
    'auth.forgot.title': 'Password reset',
    'auth.forgot.sub': 'Enter the email you signed up with. We will send a link to set a new password.',
    'auth.forgot.submit': 'Send the link',
    'auth.forgot.sent':
      'If an account with this email exists, we have sent a letter with a link. The link works for 1 hour and only once.',
    'auth.forgot.back': 'Back to sign in',
    'auth.reset.title': 'New password',
    'auth.reset.password': 'New password',
    'auth.reset.password2': 'Repeat the password',
    'auth.reset.err': 'The passwords differ or are shorter than 8 characters.',
    'auth.reset.submit': 'Save the password',
    'auth.reset.expired': 'The link has expired: it was already used or an hour has passed.',
    'auth.reset.again': 'Send again',

    'notfound.title': 'Page not found',
    'notfound.sub': 'The link is out of date or has a mistake.',
    'notfound.back': 'To objects',

    'admin.banner.role': 'Administrator',
    'admin.banner.client': "client's object: {client}",
    'admin.banner.journal': "changes go to the object's journal",
  },
  uk: {
    ...SITE_MESSAGES.uk,
    ...OPERATOR_MESSAGES.uk,
    ...ONBOARDING_MESSAGES.uk,
    ...ACCESS_MESSAGES.uk,
    'app.brand': 'Phage Energy',
    'nav.menu': 'Меню',
    'nav.lang': 'Мова',
    'nav.objects': "Об'єкти",
    'nav.applications': 'Заявки',
    'nav.prices': 'Ціни РДН',
    'nav.logout': 'Вийти',

    'common.back': 'Назад',
    'common.next': 'Далі',
    'common.cancel': 'Скасувати',
    'common.save': 'Зберегти',
    'common.close': 'Закрити',
    'common.loading': 'Завантажуємо…',
    'common.load_error': 'Не вдалося завантажити дані. Оновіть сторінку або спробуйте пізніше.',
    'error.generic': 'Не вдалося виконати дію. Спробуйте ще раз.',
    'error.rate_limited': 'Забагато спроб. Спробуйте за {min} хв.',
    'error.vendor': 'DeyeCloud не відповідає. Спробуйте за кілька хвилин.',
    'error.forbidden': 'Недоступно для вашого акаунта.',

    'auth.email': 'Email',
    'auth.password': 'Пароль',
    'auth.password.hint': 'щонайменше 8 символів',
    'auth.login.title': 'Вхід',
    'auth.login.submit': 'Увійти',
    'auth.login.forgot': 'Забули пароль?',
    'auth.login.no_account': 'Немає акаунта?',
    'auth.login.go_signup': 'Зареєструватися',
    'auth.login.expired': 'Сесія завершилась. Увійдіть знову.',
    'auth.login.reset_ok': 'Пароль змінено. Увійдіть з новим паролем.',
    'auth.login.invalid': 'Невірний email або пароль.',
    'auth.signup.title': 'Реєстрація',
    'auth.signup.sub': 'Після реєстрації підключите інвертор Deye за його SN.',
    'auth.signup.name': "Ім'я та прізвище",
    'auth.signup.phone': 'Телефон',
    'auth.signup.terms': 'Погоджуюся з умовами використання й обробкою даних',
    'auth.signup.control':
      'Дозволяю Phage.Energy керувати зарядом і розрядом батареї за розкладом, який я бачу й можу змінити або вимкнути',
    'auth.signup.consent_err': 'Щоб продовжити, погодьтеся з обома пунктами.',
    'auth.signup.submit': 'Створити акаунт',
    'auth.signup.have': 'Уже є акаунт?',
    'auth.signup.go_login': 'Увійти',
    'auth.signup.email_taken': 'Цей email уже зареєстровано. Увійдіть або скиньте пароль.',
    'auth.err.name': "Вкажіть ім'я та прізвище.",
    'auth.err.phone': 'Вкажіть телефон, наприклад +380 67 123 45 67.',
    'auth.err.email': 'Вкажіть email, наприклад owner@example.com.',
    'auth.err.password': 'Пароль — щонайменше 8 символів.',
    'auth.forgot.title': 'Скидання пароля',
    'auth.forgot.sub':
      'Вкажіть email, з яким ви реєструвалися. Надішлемо посилання, щоб задати новий пароль.',
    'auth.forgot.submit': 'Надіслати посилання',
    'auth.forgot.sent':
      'Якщо акаунт із цим email існує, ми надіслали лист із посиланням. Посилання діє 1 годину і спрацьовує один раз.',
    'auth.forgot.back': 'Повернутися до входу',
    'auth.reset.title': 'Новий пароль',
    'auth.reset.password': 'Новий пароль',
    'auth.reset.password2': 'Повторіть пароль',
    'auth.reset.err': 'Паролі не збігаються або коротші за 8 символів.',
    'auth.reset.submit': 'Зберегти пароль',
    'auth.reset.expired': 'Посилання застаріло: його вже використали або минула година.',
    'auth.reset.again': 'Надіслати ще раз',

    'notfound.title': 'Сторінку не знайдено',
    'notfound.sub': 'Посилання застаріло або містить помилку.',
    'notfound.back': "До об'єктів",

    'admin.banner.role': 'Адміністратор',
    'admin.banner.client': "об'єкт клієнта {client}",
    'admin.banner.journal': "зміни записуються в журнал об'єкта",
  },
} as const;

export type MessageKey = keyof (typeof MESSAGES)['uk'];
